import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, ShoppingBag, Loader2, Upload } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { orders as api, customers as custApi, products as prodApi } from "@/lib/api";
import { formatDate, formatIDR, platformLabel } from "@/lib/format";

const empty = { customer_id: "", product_id: "", product_name: "", order_source: "shopee", order_value: 0, reward_code_used: "" };

export default function Orders() {
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [o, c, p] = await Promise.all([api.list(), custApi.list(), prodApi.list()]);
      setRows(o); setCustomers(c); setProducts(p);
    } catch (err) { console.warn("Orders load failed:", err); }
    setLoading(false);
  };
  useEffect(() => { fetchAll(); }, []);

  const create = async () => {
    if (!form.customer_id || !form.order_value) { toast.error("Customer & nilai order wajib"); return; }
    try {
      const productObj = products.find(p => p.id === form.product_id);
      await api.create({ ...form, product_name: productObj?.product_name || form.product_name });
      toast.success("Order tercatat");
      setOpen(false); setForm(empty); fetchAll();
    } catch (err) {
      console.warn("Order create failed:", err);
      toast.error("Gagal");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900">Orders</h1>
          <p className="text-sm text-gray-500 mt-1">Catat order manual untuk tracking repeat order & profit per SKU.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/dashboard/orders/import">
            <Button variant="outline" className="border-gray-300" data-testid="import-orders-btn">
              <Upload className="mr-2 h-4 w-4" /> Import CSV
            </Button>
          </Link>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="new-order-btn"><Plus className="mr-2 h-4 w-4" /> Catat Order</Button>
            </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Catat Order Baru</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Customer</Label>
                <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                  <SelectTrigger data-testid="order-customer"><SelectValue placeholder="Pilih customer" /></SelectTrigger>
                  <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name} · {c.whatsapp_number}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Produk</Label>
                <Select value={form.product_id} onValueChange={(v) => setForm({ ...form, product_id: v })}>
                  <SelectTrigger data-testid="order-product"><SelectValue placeholder="Pilih produk (opsional)" /></SelectTrigger>
                  <SelectContent>{products.map(p => <SelectItem key={p.id} value={p.id}>{p.product_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Sumber Order</Label>
                  <Select value={form.order_source} onValueChange={(v) => setForm({ ...form, order_source: v })}>
                    <SelectTrigger data-testid="order-source"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="shopee">Shopee</SelectItem>
                      <SelectItem value="tiktok">TikTok Shop</SelectItem>
                      <SelectItem value="tokopedia">Tokopedia</SelectItem>
                      <SelectItem value="lazada">Lazada</SelectItem>
                      <SelectItem value="whatsapp">WhatsApp Direct</SelectItem>
                      <SelectItem value="offline">Offline</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Nilai Order (Rp)</Label><Input type="number" value={form.order_value} onChange={(e) => setForm({ ...form, order_value: Number(e.target.value) })} data-testid="order-value" /></div>
              </div>
              <div><Label>Kode Reward (opsional)</Label><Input value={form.reward_code_used} onChange={(e) => setForm({ ...form, reward_code_used: e.target.value })} data-testid="order-reward" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
              <Button onClick={create} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-order-btn">Simpan</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card className="border-gray-200 overflow-hidden">
        {loading ? <div className="p-16 flex justify-center"><Loader2 className="animate-spin text-emerald-600" /></div>
          : rows.length === 0 ? (
            <div className="py-16 text-center">
              <ShoppingBag className="mx-auto text-gray-300" size={56} />
              <h3 className="mt-4 font-heading text-lg font-semibold text-gray-900">Belum ada order</h3>
              <p className="mt-1 text-sm text-gray-500">Catat order untuk mulai tracking repeat order rate & profit.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3">Tanggal</th>
                    <th className="px-4 py-3">Customer ID</th>
                    <th className="px-4 py-3">Produk</th>
                    <th className="px-4 py-3">Sumber</th>
                    <th className="px-4 py-3">Nilai</th>
                    <th className="px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((o) => (
                    <tr key={o.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-gray-700">{formatDate(o.order_date)}</td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-500">{o.customer_id.slice(0, 8)}</td>
                      <td className="px-4 py-3 text-gray-900">{o.product_name || "-"}</td>
                      <td className="px-4 py-3 text-gray-700">{platformLabel(o.order_source)}</td>
                      <td className="px-4 py-3 font-semibold text-gray-900">{formatIDR(o.order_value)}</td>
                      <td className="px-4 py-3">
                        {o.is_repeat_order ? (
                          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border">Repeat</Badge>
                        ) : (
                          <Badge className="bg-blue-50 text-blue-700 border-blue-200 border">First order</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </Card>
    </div>
  );
}
